import {createGeoJSONGraphic} from './GeoJSONGraphic';
import {createKMLGraphic} from './KMLGraphic';
import {createREPGraphic} from './REPGraphic';
import {createImageGraphic} from './ImageGraphic/createImageGraphic';
import {createPlacesGraphic} from './PlacesGraphic';
import {createAccessRouteGraphic} from './AccessRouteGraphic/createAccessRouteGraphic';
import {createDroneGraphic} from './DroneGraphic';
import {MapService} from '../MapService';
import {createPolygonGraphic} from './PolyGraphic/PolygonGraphic';
import {createPolylineGraphic} from './PolyGraphic/PolylineGraphic';
import {createLabelGraphic} from './LabelGraphic/LabelGraphic';
import {createPlaceMarkerGraphic} from './PlaceMarkerGraphic/PlaceMarkerGraphic';
import {createBlockPlanGraphic} from './BlockPlanGraphic/createBlockPlanGraphic';

/**
 * @typedef {import("./Graphic").Graphic} Graphic
 * @typedef {import("./WMTSTileGraphic").WMTSTileGraphic} WMTSTileGraphic
 * @typedef {import("./VectorTileGraphic/VectorTileGraphic").VectorTileGraphic} VectorTileGraphic
 */

/**
 * @private
 * @type {Map<string, Map<string, Graphic>>}
 */
const _graphics = new Map();

/**
 * @private
 * @type {boolean}
 */
let _clickabilityMode = true;

/**
 * @private
 * @type {boolean}
 */
let _renderMode = true;

/**
 * @private
 * @type {boolean}
 */
let _pointerEventsEnabled = true;

/**
 * @typedef {object} GraphicData Object with data needed to construct a graphic.
 * @property {string} featureType A tag indicating the type of graphic this is.
 */

/**
 * @private
 * @type {Record<GraphicTypeTag, (feature: GraphicData) => Promise<Graphic>>}
 */
const _typeMappings = {
  'png': createImageGraphic,
  'jpg': createImageGraphic,
  'kmz': createKMLGraphic,
  'geojson': createGeoJSONGraphic,
  'rep': createREPGraphic,
  'places': createPlacesGraphic,
  'route': createAccessRouteGraphic,
  'polygon': createPolygonGraphic,
  'polyline': createPolylineGraphic,
  'drone': createDroneGraphic,
  'label': createLabelGraphic,
  'placeMarker': createPlaceMarkerGraphic,
  'blockPlan-crossDock': createBlockPlanGraphic,
  'blockPlan-singleLoad': createBlockPlanGraphic,
};

/** Exported functions for manipulating graphics. */
const Graphics = {
  /**
   * Creates a new graphic of the chosen type and register it in GraphicService under the chosen group and id.
   * If the group does not exist, it will be created.
   * If a graphic already exists under the chosen group and id, it will be un-rendered and replaced.
   * @async
   * @param {string} group The group under which to create the graphic.
   * @param {string} id The id under which to create the graphic.
   * @param {GraphicData} data The data from which to create the graphic.
   * @param {boolean} [visible=true] Whether the graphic should be visible when added.
   * @return {Promise<Graphic>} A promise resolving to the created graphic.
   * @throws {TypeError} If `type` does not resolve to an implemented graphic builder function.
   */
  create: async function(group, id, data, visible = true) {
    // CODE REVIEW: This doesn't feel great to do here. But ultimately, we shouldn't need to use a uuid like we
    // previously were for the marker ID, since we already have a unique ID for each feature from Firestore.
    if (data.featureType === 'placeMarker') {
      // The ID for placeMarker features is their document ID in Firestore
      data.id = id;
    }

    /** @type {GraphicTypeTag} */
    const type = data.featureType;
    if (!type) throw new Error(`Cannot create graphic '${id}': the featureType must be included in the graphic data.`);
    const createFn = _typeMappings[type];
    if (!createFn) throw new TypeError(`Cannot create graphic '${id}' of type '${type}' because that type is not implemented.`);
    /** @type {Promise<Graphic>} */
    const graphicPromise = createFn(data);

    // Create the group if it does not exist.
    if (!_graphics.has(group)) {
      _graphics.set(group, new Map());
    } else if (_graphics.get(group).has(id)) {
      // If a graphic already exists under this group & id, unrender it before replacing it.
      _graphics.get(group).get(id).setMap(null);
    }
    // Register the graphic once it's available.
    /** @type {Graphic} */
    let graphic;
    try {
      graphic = await graphicPromise;
    } catch (err) {
      err.message = `Unable to create graphic '${id}': ${err.message}`;
      throw err;
    }
    _graphics.get(group).set(id, graphic);

    // Set properties of the graphic according to the global settings.
    graphic.setClickable(_clickabilityMode);
    graphic.setMap(visible ? _getMapFromRender(_renderMode) : null);
    graphic.type = data.featureType;

    return graphic;
  },

  /**
   * Registers a graphic in GraphicService under the chosen group and id.
   * If the group does not exist, it will be created.
   * Several of our layer types (e.g. TaxParcelGraphic, VectorTileGraphic, WMTSTileGraphic) are created outside of
   * the GraphicService, such as in MapLayer.ts, then passed to this function for registration with the GraphicService.
   * @param {string} group The group under which to register the graphic.
   * @param {string} id The id under which to register the graphic.
   * @param {Graphic | VectorTileGraphic | WMTSTileGraphic} graphic The graphic to register.
   */
  register: function(group, id, graphic) {
    if (!graphic) {
      throw new Error('Cannot register a null graphic');
    }
    if (!_graphics.has(group)) {
      _graphics.set(group, new Map());
    }
    _graphics.get(group).set(id, graphic);
  },

  /**
   * Deletes a graphic from the map and from the GraphicService.
   * @param {*} group the group to delete the graphic from
   * @param {*} id the id of the graphic to delete
   */
  delete: function(group, id) {
    _checkEntryExists('Unable to delete one or more graphics', group, id);
    _graphics.get(group).get(id).setMap(null);
    _graphics.get(group).delete(id);
  },

  /**
   * Checks whether a group exists or whether a graphic exists under a chosen group and id.
   * If `id` is omitted, checks the existence of `group`. If `id` is included, checks the existence of a graphic
   * under the chosen group and id.
   * @param {string} group The group to check.
   * @param {string} [id] The id to check.
   * @return {boolean} `true` if the group/graphic exists, otherwise `false`.
   */
  exists: function (group, id) {
    return _graphics.has(group) && (!id || _graphics.get(group).has(id));
  },

  /**
   * Sets the clickability of all graphics within a chosen group.
   * @param {string} group The group whose graphics to affect.
   * @param {boolean} clickable `true` to make the graphics clickable; `false` to make them un-clickable.
   * @throws {ReferenceError} If `group` does not exist.
   */
  setClickable: function(group, clickable) {
    _checkEntryExists('Unable to set clickability for one or more graphics', group);
    _graphics.get(group).forEach((graphic) => graphic.setClickable(clickable));
  },

  /**
   * Sets the clickability of all graphics in all groups, and sets the default clickability for new graphics.
   * @param {boolean} clickable `true` to make the graphics clickable; `false` to make them un-clickable.
   */
  setAllClickable: function(clickable) {
    _clickabilityMode = clickable;
    _graphics.forEach((group) =>
      group.forEach((graphic) =>
        graphic.setClickable(clickable),
      ),
    );
  },

  /**
   * Controls whether pointer events are enabled for all graphics.
   * @param {boolean} enabled `true` sets pointer events to `auto`, and `false` sets pointer events to `none`.
   */
  setAllPointerEvents: function(enabled = true) {
    if (_pointerEventsEnabled === enabled) return;
    _pointerEventsEnabled = enabled;

    _graphics.forEach((group) => {
      group.forEach((graphic) => {
        graphic?.setPointerEvents?.(_pointerEventsEnabled);
      });
    });
  },

  /**
   * Makes public the state of all graphics clickability
   * @return {boolean} true when graphics are clickable, false when they are not
   */
  areClickable: function() {
    return _clickabilityMode;
  },

  /**
   * Sets the rendering behavior of all graphics within a chosen group.
   * @param {string} group The group whose graphics to affect.
   * @param {boolean} render `true` to render the graphics to the map; `false` to remove them from the map.
   * @param {string} path
   * @throws {ReferenceError} If `group` does not exist.
   */
  setRender: function(group, render, path = null) {
    _checkEntryExists('Unable to set render status for one or more graphics', group);
    if (path) {
      _graphics.get(group).get(path).setMap(_getMapFromRender(render));
      return;
    }
    _graphics.get(group).forEach((graphic) => {
      graphic.setMap(_getMapFromRender(render));
    });
  },

  /**
   * Sets the rendering behavior of all graphics in all groups and the default rendering behavior for new graphics.
   * @param {boolean} render `true` to render the graphics to the map; `false` to remove them from the map.
   */
  setAllRender: function(render) {
    _renderMode = render;
    _graphics.forEach((group) =>
      group.forEach((graphic) =>
        graphic.setMap(_getMapFromRender(render)),
      ),
    );
  },

  /**
   * Adds a handler to a chosen graphic.
   * @param {string} group The group containing the chosen graphic.
   * @param {string} id The id of the chosen graphic.
   * @param {string} event The event type to handle.
   * @param {string} handlerName The name identifying this handler.
   * @param {Function} handler The function to handle the event.
   * @throws {ReferenceError} If a graphic does not exist under `group` & `id`.
   */
  addHandler: function(group, id, event, handlerName, handler) {
    _checkEntryExists('Unable to add handler', group, id);
    _graphics.get(group).get(id).addHandler(event, handlerName, handler);
  },
  /**
   * Returns the specified graphic from a group, if it exists.
   * @param {string} group The group containing the chosen graphic.
   * @param {string} id The id of the chosen graphic.
   * @return {Graphic} The desired graphic
   */
  getGraphic: function(group, id) {
    _checkEntryExists('Unable to get graphic', group, id);
    return _graphics.get(group).get(id);
  },
};

/**
 * Converts the boolean for whether to render graphic(s) into a Map object or lack thereof.
 * @param {boolean} render `true` if the graphic(s) are being rendered, `false` otherwise.
 * @return {google.maps.Map|null} The current Map if rendering the graphic(s), else `null`.
 */
function _getMapFromRender(render) {
  return render ? MapService.getMap() : null;
}

/**
 * @param {string} errPrefix Prefix for the error message if the check fails.
 * @param {string} group The group to check.
 * @param {string} [id] (optional) The ID of the graphic to check.
 * @throws {ReferenceError} if the graphic group does not exist, or if the ID does not exist
 */
function _checkEntryExists(errPrefix, group, id) {
  if (!_graphics.has(group)) {
    throw new ReferenceError(`${errPrefix}: graphics group ${group} does not exist`);
  }
  if (id !== undefined && !_graphics.get(group).has(id)) {
    throw new ReferenceError(`${errPrefix}: graphic in group ${group} with id ${id} does not exist`);
  }
}

/**
 * Helper function to wrap a mousedown event listener such that the event will not fire unless the mouse moves no more
 * than `deadband` pixels away before the mouseup event occurs.
 * @param {function(Event): void} listener The event listener to wrap.
 * @param {number} [deadband] The range in pixels within which the cursor must stay for the listener to fire.
 * @return {function(Event): void} The wrapped listener.
 */
const wrapListenerForDeadband = (listener, deadband = 10) => {
  return (evt, ...args) => {
    const target = evt.currentTarget;
    let {clientX: lastX, clientY: lastY} = evt;
    // Monitor mouse movement to determine if dragging and remove the event listeners
    let accumulator = 0;
    const handleMouseMove = (evt) => {
      const deltaX = evt.clientX - lastX;
      const deltaY = evt.clientY - lastY;
      accumulator += Math.hypot(deltaX, deltaY);
      if (accumulator > deadband) {
        target.removeEventListener('mousemove', handleMouseMove);
        target.removeEventListener('mouseup', handleMouseUp);
        return;
      }
      lastX = evt.clientX;
      lastY = evt.clientY;
    };
    const handleMouseUp = (evt) => {
      // This additional check is needed in case a mousemove and mouseup occur on the same tick
      if (accumulator > deadband) {
        return;
      }
      listener(evt, ...args);
    };
    target.addEventListener('mousemove', handleMouseMove);
    target.addEventListener('mouseup', handleMouseUp, {once: true});
  };
};

export {
  Graphics,
  _graphics, // Should not be used for anything other than testing
  wrapListenerForDeadband,
};
